import * as React from 'react'
import { Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Root from '@components/root/Root'
import Button from '@components/button/Button'
import IncidentDetailComponent from './IncidentDetailComponent'
import MessageAlert from './MessageAlert'
import RefuseForm from './RefuseForm'
import SelectTechnician from './SelectTechnician'
import CommentAndFileInput from './CommentAndFileInput'
import Drawer from '@components/drawer/Drawer'
import Modal from '@components/modal/Modal'
import SimpleAlert from '@components/modal/SimpleAlert'
import { RadioOption } from '@components/radio/Radio'
import Icon from '@components/icon/Icons'

import api from '@api/api'

import useReducer from '@store/useReducer'
import * as SocieteStore from '@store/societe/societe'
import * as UserStore from '@store/user/user'
import * as SiteStore from '@store/site/site'
import * as AuthStore from '@store/auth/auth'
import * as LocationStore from '@store/location/location'
import * as IncidentStore from '@store/incident/incident'
import * as FiltreStore from '@store/filtre/filtre'
import { emptyFilters } from '@store/filtre/filtre'

import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'
import useBreakpoint from '@utils/useBreakpoints'
import useWorkflow from '@screens/incident/useWorkflow'

import { isBefore } from 'date-fns'
import logger from '@utils/logger'
import { findLocalisationParents } from '@screens/incident/utils'

import { AuthScope, EtatWorkflowIncidentEnum } from '@api/enum'
import { paramsToSearch } from '@api/utils'

interface BottomButton {
  label: string
  status: ButtonStatus
  onClick: () => void
  color?: ButtonColor
}

const openSuccessAlert = (i18n: I18nState, i18nKey?: string) =>
  MessageAlert.open({
    title: i18n.t('common.success'),
    description: i18n.t(`screens.incidentDetail.success.${i18nKey || 'default'}`),
    onFinished: MessageAlert.close,
  })

const openErrorAlert = (i18n: I18nState) =>
  MessageAlert.open({
    title: i18n.t('common.errorTitle'),
    description: i18n.t(`common.error`),
    onFinished: MessageAlert.close,
  })

interface Props {
  incidentId: string
  edlId?: string
}

const IncidentDetailScreen = (props: Props) => {
  const user = useReducer(UserStore.store, (s) => s.user)

  // screen available only for connected user
  if (!user) {
    return null
  }

  return <Screen {...props} user={user} />
}

interface ScreenProps extends Props {
  user: UserResponse
}

const Screen = ({ incidentId, user, edlId }: ScreenProps) => {
  const i18n = useI18n()
  const navigation = useNavigation()

  const favoriteIncidents = useReducer(UserStore.store, (s) => s.favoriteIncidents)
  const technicians = useReducer(SocieteStore.store, (s) => s.technicians)
  const site = useReducer(SiteStore.store, (s) => s.site)
  const scopes = useReducer(AuthStore.store, (s) => s.scopes)
  const places = useReducer(LocationStore.store, (s) => s.places)
  const incidents = useReducer(IncidentStore.store, (s) => s.incidents)

  const [{ width }, bp] = useBreakpoint()
  const isPhone = bp === 'phone'

  const [status, setStatus] = React.useState<Status>('loading')
  const [commentStatus, setCommentStatus] = React.useState<Status>('loading')
  const [submitStatus, setSubmitStatus] = React.useState<Status>('fetched')
  const [followStatus, setFollowStatus] = React.useState<Status>('fetched')
  const [editIncidentStatus, setEditIncidentStatus] = React.useState<Status>('fetched')

  const [incident, setIncident] = React.useState<IncidentResponse>()
  const [comments, setComments] = React.useState<CommentaireResponse[]>([])

  const [isPrioritaire, setIsPrioritaire] = React.useState(false)
  const [prestataireId, setPrestataireId] = React.useState<string>('')

  const form = useReducer(IncidentStore.store, (s) => s.form)
  const [, starter] = useWorkflow(form, { withIncidentToCopy: incident })

  const space = places.find((p) => p.id === incident?.localisationId)
  const locationParents = space && findLocalisationParents(places, space)

  const isIncidentFollowed = incident && favoriteIncidents.map((f) => f.id).includes(incident.id)
  const canCopyIncident = scopes.includes(AuthScope.DemandesIncidentCreation)

  React.useEffect(() => {
    if (site) {
      api.societe
        .all(site.id)
        .then((res) => SocieteStore.actions.setServices(res))
        .catch(logger.error)

      fetchFullIncident()
      fetchComments()
    }
  }, [site])

  React.useEffect(() => {
    if (!!incident && incident.societePrestataireId && scopes.includes(AuthScope.DemandesTechnicienAffectation)) {
      api.users
        .all(incident.societePrestataireId)
        .then((res) => SocieteStore.actions.setTechnicians(res.sort((a, b) => a.prenom.localeCompare(b.prenom))))
        .catch(logger.error)
    }
  }, [incident])

  React.useEffect(() => {
    Drawer.close()
    Modal.close()
  }, [isPhone])

  React.useEffect(() => {
    if (incident) {
      setIsPrioritaire(!!incident.isPrioritaire)
      setPrestataireId(incident.societePrestataireId || '')
    }
  }, [incident])

  const techniciansOptions: RadioOption[] = React.useMemo(
    () => [
      {
        label: i18n.t('screens.incidentDetail.selectTechnician.you', {
          firstName: user.prenom,
          lastName: user.nom,
        }),
        value: { id: user.id, prenom: user.prenom, nom: user.nom, tel: user.numeroTelephonePortable },
      },
      ...technicians.filter((t) => t.id !== user.id).map((t) => ({ label: `${t.prenom} ${t.nom}`, value: t })),
    ],
    [technicians]
  )

  const updateIncidentList = (next: IncidentResponse) => {
    IncidentStore.actions.setIncidents(incidents.map((i) => (i.id === next.id ? next : i)))
  }

  const fetchFullIncident = () => {
    setStatus('loading')
    const stored = incidents.find((inc) => inc.id === incidentId)

    if (stored) {
      setIncident(stored)
      setStatus('fetched')
    } else {
      return api.incident
        .get(incidentId)
        .then((res) => {
          setIncident(res)
          setStatus('fetched')
        })
        .catch((err) => {
          logger.error(err)
          setStatus('error')
        })
    }
  }

  const fetchComments = () => {
    setCommentStatus('loading')

    return api.incident
      .comments(incidentId)
      .then((res) => {
        setComments(res.sort((a, b) => (isBefore(new Date(a.creationDate), new Date(b.creationDate)) ? -1 : 1)))
        setCommentStatus('fetched')
      })
      .catch(logger.error)
  }

  const submitComment = (commentaire: string, images: PieceJointeCommentaireRequest[]) => {
    setSubmitStatus('loading')
    return api.incident
      .comment(incidentId, { commentaire, commentaireType: 'COMMENTAIRE' as CommentaireType })
      .then((res) => {
        setSubmitStatus('fetched')
        return submitAttachedFiles(res.id, images)
      })
      .then(() => fetchComments())
      .catch(() => {
        setSubmitStatus('error')
        return Promise.reject()
      })
  }

  const submitAttachedFiles = (commentaireId: string, images: PieceJointeCommentaireRequest[]) => {
    if (images.length > 0) {
      return Promise.all(images.map((i) => api.incident.addFile(incidentId, commentaireId, i)))
    }
  }

  const onCopyIncident = () => {
    if (incident) {
      IncidentStore.actions.resetForm()
      IncidentStore.actions.setForm(starter)
      navigation.push(`/incident/recap${paramsToSearch({ copy: true })}`)
    }
  }

  const onFollowButtonClicked = () => {
    setFollowStatus('loading')
    if (!!incident) {
      if (!isIncidentFollowed) {
        api.incident
          .follow(incidentId)
          .then(() => UserStore.actions.addIncidentToFavorites(incident))
          .then(() => setFollowStatus('fetched'))
          .catch(() => setFollowStatus('error'))
      } else {
        api.incident
          .unfollow(incidentId)
          .then(() => UserStore.actions.removeIncidentFromFavorites(incident))
          .then(() => setFollowStatus('fetched'))
          .catch(() => setFollowStatus('error'))
      }
    }
  }

  const navigateToMap = () => {
    if (locationParents) {
      const location = locationParents[0].enabled ? [locationParents[0].id] : []

      FiltreStore.actions.setIncidentFilter({ ...emptyFilters, location })
    }
    navigation.push('/plan', { externalIncident: [incident] })
  }

  const onConfirm = (apiCall: () => Promise<IncidentResponse>, i18nKey: string) =>
    apiCall()
      .then((res) => {
        openSuccessAlert(i18n, i18nKey)
        setIncident(res)
        updateIncidentList(res)
        fetchComments()
      })
      .catch((err) => {
        logger.error(err)
        openErrorAlert(i18n)
      })
      .finally(() => setEditIncidentStatus('fetched'))

  const openConfirm = (apiCall: () => Promise<IncidentResponse>, i18nKey: string) => {
    SimpleAlert.open({
      title: i18n.t('common.confirm'),
      description: i18n.t(`screens.incidentDetail.confirm.${i18nKey}`),
      buttons: [
        {
          label: i18n.t('common.cancel'),
          color: 'cancel',
          onClick: () => {
            SimpleAlert.close()
            setEditIncidentStatus('fetched')
          },
        },
        {
          label: i18n.t('common.validate'),
          onClick: () => {
            onConfirm(apiCall, i18nKey)
            SimpleAlert.close()
          },
        },
      ],
      cancel: false,
    })
  }

  const openInModal = (render: () => React.ReactElement) => {
    if (isPhone) {
      Drawer.open({
        render,
        hideTarget: true,
        backgroundColor: theme.colors.incident,
      })
    } else {
      Modal.open({
        Content: render,
      })
    }
  }

  const handleApiCall = (apiCall: () => Promise<IncidentResponse>, i18nKey: string, withoutConfirm?: boolean) => {
    setEditIncidentStatus('loading')

    if (!withoutConfirm) {
      openConfirm(apiCall, i18nKey)
    } else {
      onConfirm(apiCall, i18nKey)
    }
  }

  const affectTechnician = (technician: UserResponse, onlyOneChoice?: boolean) =>
    handleApiCall(
      () => api.incident.affect(incidentId, { technicienId: technician.id }),
      onlyOneChoice ? 'affectOnlyTechnician' : 'affectTechnician'
    )

  const reassignProvider = (societePrestataireId: string) => {
    api.incident
      .reassignProvider(incidentId, { societePrestataireId })
      .then((res) => {
        if (res.societePrestataireId) {
          setPrestataireId(res.societePrestataireId)
        }

        setIncident(res)
        updateIncidentList(res)
      })
      .then(fetchComments)
      .then(() =>
        SimpleAlert.open({
          title: i18n.t('screens.incidentDetail.success.changePrestataire'),
          buttons: [{ label: i18n.t('common.ok'), onClick: () => SimpleAlert.close() }],
        })
      )
      .catch(() =>
        SimpleAlert.open({
          title: i18n.t('screens.incidentDetail.errors.reassignProviderTitle'),
          description: i18n.t('screens.incidentDetail.errors.reassignProviderBody'),
          buttons: [{ label: i18n.t('screens.incidentDetail.errors.ok'), onClick: () => SimpleAlert.close() }],
        })
      )
  }

  const refuse = (response: Omit<AcceptIncidentRequest, 'utilisateurId'>) => {
    const body = {
      ...response,
      utilisateurId: user.id,
    }
    handleApiCall(() => api.incident.accept(incidentId, body), 'refuse')
  }

  const onTreat = () => handleApiCall(() => api.incident.acceptPrestataire(incidentId), 'treat')

  const onCloseByProvider = () => handleApiCall(() => api.incident.closePrestataire(incidentId), 'closeByProvider')

  const onStandInaccessible = (body: StandInaccessibleRequest | EnvoieDevisIncidentRequest) =>
    handleApiCall(
      () => api.incident.inaccessibleStand(incidentId, body as StandInaccessibleRequest),
      'standInaccessible'
    )

  const onQuoteSend = (response: EnvoieDevisIncidentRequest | StandInaccessibleRequest) =>
    handleApiCall(() => api.incident.sendQuote(incidentId, response as EnvoieDevisIncidentRequest), 'sendQuote')

  const onIncidentValidation = (validated: boolean, body?: ValidateIncidentRequest) => {
    if (body && !body.prestataireId) {
      SimpleAlert.open({
        title: i18n.t('screens.incidentDetail.errors.providerRequiredTitle'),
        description: i18n.t('screens.incidentDetail.errors.providerRequired'),
        buttons: [{ label: i18n.t('common.ok'), onClick: () => SimpleAlert.close() }],
      })
    } else {
      handleApiCall(
        () => (validated ? api.incident.validate(incidentId, body!) : api.incident.abandon(incidentId)),
        validated ? 'validated' : 'abandon'
      )
    }
  }

  const onAcceptQuote = () => handleApiCall(() => api.incident.treat(incidentId, { action: 'ACCEPTE' }), 'acceptQuote')

  const onRefuseQuote = () => handleApiCall(() => api.incident.treat(incidentId, { action: 'REFUSE' }), 'refuseQuote')

  const onClose = () => handleApiCall(() => api.incident.closeViparis(incidentId), 'close')

  const onSendBackToProvider = () => handleApiCall(() => api.incident.restart(incidentId), 'sendBack')

  const onReopenToProvider = () =>
    handleApiCall(
      () =>
        incident && incident.societePrestataireId
          ? api.incident.reassignProvider(incidentId, { societePrestataireId: incident.societePrestataireId })
          : Promise.reject(),
      'sendBack'
    )

  const onProviderRelaunch = () => {
    setEditIncidentStatus('loading')

    api.incident
      .comment(incidentId, { commentaire: '', commentaireType: 'RELANCE_PRESTATAIRE' as CommentaireType })
      .then(() =>
        MessageAlert.open({
          title: i18n.t(`screens.incidentDetail.alerts.relaunchProviderTitle`),
          description: i18n.t(`screens.incidentDetail.alerts.relaunchProviderDescription`),
          onFinished: MessageAlert.close,
        })
      )
      .then(() => fetchComments())
      .then(() => fetchFullIncident())
      .catch((err) => {
        logger.error(err)
        openErrorAlert(i18n)
      })
      .finally(() => setEditIncidentStatus('fetched'))
  }

  const onAffect = () => {
    if (techniciansOptions.length === 0) {
      openErrorAlert(i18n)
    } else if (techniciansOptions.length > 1) {
      openInModal(() => renderSelectTechnician(affectTechnician))
    } else {
      affectTechnician(techniciansOptions[0].value, true)
    }
  }

  const copyButton: HeaderButton & { id: string } = {
    id: 'copyButton',
    label: i18n.t(`screens.incidentDetail.copy`),
    onClick: onCopyIncident,
    leftIcon: 'add',
    color: 'default',
  }

  const followButton: HeaderButton & { id: string } = {
    id: 'followButton',
    label: i18n.t(`screens.incidentDetail.${isIncidentFollowed ? 'unfollow' : 'follow'}`),
    onClick: onFollowButtonClicked,
    leftIcon: isIncidentFollowed ? 'love-filled' : 'fav',
    color: isIncidentFollowed ? 'default' : 'light',
    status: followStatus === 'fetched' ? 'active' : 'loading',
  }

  const mapButton: HeaderButton & { id: string } = {
    id: 'mapButton',
    label: i18n.t('screens.incidentDetail.seeOnMap'),
    onClick: navigateToMap,
    color: 'light',
    status: followStatus === 'fetched' ? 'active' : 'loading',
  }

  const headerButtons: HeaderButton[] = [followButton, mapButton].filter(
    (b) => !!incident?.plan?.x || b.id !== 'mapButton'
  )

  const ActionAbandonner: BottomButton = {
    label: i18n.t('screens.incidentDetail.buttons.abandon'),
    status: editIncidentStatus === 'fetched' ? 'active' : 'loading',
    onClick: () => onIncidentValidation(false),
    color: 'cancel',
  }

  // **********************************************
  //             VALIDATION VIPARIS
  // **********************************************

  const ActionValidationRefuser: BottomButton = {
    label: i18n.t('screens.incidentDetail.close'),
    status: editIncidentStatus === 'fetched' ? 'active' : 'loading',
    onClick: () => onIncidentValidation(false),
    color: 'light',
  }

  const ActionValidationAccepter: BottomButton = {
    label: i18n.t('screens.incidentDetail.validate'),
    status: editIncidentStatus === 'fetched' ? 'active' : 'loading',
    onClick: () => onIncidentValidation(true, { validate: true, isPrioritaire, prestataireId: prestataireId || '' }),
  }

  const ActionRenvoyerPrestataire: BottomButton = {
    label: i18n.t('screens.incidentDetail.sendBackToProvider'),
    status: editIncidentStatus === 'fetched' ? 'active' : 'loading',
    onClick: onReopenToProvider,
  }

  // **********************************************
  //         PRISE EN CHARGE PRESTATAIRE
  // **********************************************

  const ActionPrestataireRefuser: BottomButton = {
    label: i18n.t('screens.incidentDetail.buttons.refuse'),
    status: editIncidentStatus === 'fetched' ? 'active' : 'loading',
    onClick: () => openInModal(renderRefuseForm),
    color: 'light',
  }

  const ActionPrestataireAccepter: BottomButton = {
    label: i18n.t('screens.incidentDetail.buttons.accept'),
    status: editIncidentStatus === 'fetched' ? 'active' : 'loading',
    onClick: onAffect,
  }

  const ActionPrestataireReaffecter: BottomButton = {
    label: i18n.t('screens.incidentDetail.buttons.reassign'),
    status: editIncidentStatus === 'fetched' ? 'active' : 'loading',
    onClick: onAffect,
    color: 'cancel',
  }

  const ActionPrestatairePrendreEnCharge: BottomButton = {
    label: i18n.t('screens.incidentDetail.buttons.treat'),
    status: editIncidentStatus === 'fetched' ? 'active' : 'loading',
    onClick: onTreat,
  }

  // **********************************************
  //               ENVOI DE DEVIS
  // **********************************************

  const ActionDevisEnvoyer: BottomButton = {
    label: i18n.t('screens.incidentDetail.buttons.sendQuote'),
    status: editIncidentStatus === 'fetched' ? 'active' : 'loading',
    onClick: () => openInModal(renderSendQuote),
  }

  // **********************************************
  //            TRAITEMENT DE DEVIS
  // **********************************************

  const ActionDevisRefuser: BottomButton = {
    label: i18n.t('screens.incidentDetail.buttons.refuseQuote'),
    status: editIncidentStatus === 'fetched' ? 'active' : 'loading',
    onClick: onRefuseQuote,
    color: 'light',
  }

  const ActionDevisAccepter: BottomButton = {
    label: i18n.t('screens.incidentDetail.buttons.acceptQuote'),
    status: editIncidentStatus === 'fetched' ? 'active' : 'loading',
    onClick: onAcceptQuote,
  }

  // **********************************************
  //            TRAITEMENT PRESTATAIRE
  // **********************************************

  const ActionPauseStandInaccessible: BottomButton = {
    label: i18n.t('screens.incidentDetail.buttons.standInaccessible'),
    status: editIncidentStatus === 'fetched' ? 'active' : 'loading',
    onClick: () => openInModal(renderStandInaccessible),
    color: 'light',
  }

  const ActionPrestataireCloturer: BottomButton = {
    label: i18n.t('screens.incidentDetail.buttons.close'),
    status: editIncidentStatus === 'fetched' ? 'active' : 'loading',
    onClick: onCloseByProvider,
  }

  // **********************************************
  //            CLOTURE PRESTATAIRE
  // **********************************************

  const ActionReouvrir: BottomButton = {
    label: i18n.t('screens.incidentDetail.buttons.sendBack'),
    status: editIncidentStatus === 'fetched' ? 'active' : 'loading',
    onClick: onSendBackToProvider,
    color: 'light',
  }

  const ActionViparisCloturer: BottomButton = {
    label: i18n.t('screens.incidentDetail.buttons.close'),
    status: editIncidentStatus === 'fetched' ? 'active' : 'loading',
    onClick: onClose,
  }

  // **********************************************
  //            EN PAUSE
  // **********************************************

  const ActionPrestataireReprendre: BottomButton = {
    label: i18n.t('screens.incidentDetail.buttons.treat'),
    status: editIncidentStatus === 'fetched' ? 'active' : 'loading',
    onClick: onTreat,
  }

  const getActions = (status: EtatWorkflowIncidentEnum, scps: globalThis.AuthScope[]): BottomButton[] => {
    const actions: BottomButton[] = []

    switch (status) {
      case EtatWorkflowIncidentEnum.EN_ATTENTE_DE_VALIDATION:
        if (scps.includes(AuthScope.DemandesWorkflowAbandon)) {
          // AuthScope.DemandesWorkflowAbandon -> can refuse and close
          actions.push(ActionValidationRefuser)
        }

        if (scps.includes(AuthScope.DemandesWorkflowValidation)) {
          // AuthScope.DemandesWorkflowValidation -> can validate
          actions.push(ActionValidationAccepter)
        }

        break

      case EtatWorkflowIncidentEnum.EN_ATTENTE_DE_PRISE_EN_CHARGE:
        if (scps.includes(AuthScope.DemandesWorkflowAbandon)) {
          // AuthScope.DemandesWorkflowAbandon -> can refuse and close
          actions.push(ActionAbandonner)
        }

        if (!!incident?.technicienAffecteId) {
          if (scps.includes(AuthScope.DemandesTechnicienAffectation)) {
            // AuthScope.DemandesTechnicienAffectation -> can change affected technician
            actions.push(ActionPrestataireReaffecter)
          }

          if (incident.technicienAffecteId === user.id && scps.includes(AuthScope.DemandesWorkflowPriseEnCharge)) {
            // AuthScope.DemandesWorkflowPriseEnCharge -> can start handle if user is affected technician
            actions.push(ActionPrestatairePrendreEnCharge)
          }
        } else {
          if (scps.includes(AuthScope.DemandesWorkflowAccepter)) {
            // AuthScope.DemandesWorkflowFermeturePrestataire -> can accept handle without closing
            actions.push(ActionPrestataireAccepter)
          }
          if (scps.includes(AuthScope.DemandesWorkflowRefuser)) {
            // AuthScope.DemandesWorkflowFermeturePrestataire -> can refuse handle without closing
            actions.push(ActionPrestataireRefuser)
          }
        }

        break

      case EtatWorkflowIncidentEnum.REFUS_PRESTATAIRE:
        if (scps.includes(AuthScope.DemandesWorkflowAbandon)) {
          // AuthScope.DemandesWorkflowAbandon -> can refuse and close
          actions.push(ActionAbandonner)
        }

        if (scps.includes(AuthScope.DemandesDevisCreation) && scps.includes(AuthScope.DemandesWorkflowValidation)) {
          actions.push(ActionRenvoyerPrestataire)
        }

        break

      case EtatWorkflowIncidentEnum.EN_ATTENTE_DE_DEVIS:
        if (scps.includes(AuthScope.DemandesWorkflowRefuser)) {
          // AuthScope.DemandesWorkflowFermeturePrestataire -> can refuse handle
          actions.push(ActionPrestataireRefuser)
        }

        if (scps.includes(AuthScope.DemandesWorkflowAbandon)) {
          // AuthScope.DemandesWorkflowAbandon -> can refuse and close
          actions.push(ActionAbandonner)
        }

        if (scps.includes(AuthScope.DemandesWorkflowEnvoiDevis)) {
          // AuthScope.DemandesWorkflowEnvoiDevis -> can send quote
          actions.push(ActionDevisEnvoyer)
        }

        break

      case EtatWorkflowIncidentEnum.DEVIS_ENVOYE:
        if (scps.includes(AuthScope.DemandesWorkflowAbandon)) {
          // AuthScope.DemandesWorkflowAbandon -> can refuse and close
          actions.push(ActionAbandonner)
        }

        if (scps.includes(AuthScope.DemandesWorkflowTraitementDevis)) {
          // AuthScope.DemandesWorkflowTraitementDevis -> can accept or refuse quote
          actions.push(ActionDevisRefuser)
          actions.push(ActionDevisAccepter)
        }

        break

      case EtatWorkflowIncidentEnum.EN_COURS_DE_TRAITEMENT:
        if (scps.includes(AuthScope.DemandesWorkflowAbandon)) {
          // AuthScope.DemandesWorkflowAbandon -> can refuse and close
          actions.push(ActionAbandonner)
        }

        if (scps.includes(AuthScope.DemandesWorkflowMiseEnPause)) {
          // AuthScope.DemandesWorkflowMiseEnPause -> can pause handle
          actions.push(ActionPauseStandInaccessible)
        }

        if (scps.includes(AuthScope.DemandesWorkflowFermeturePrestataire)) {
          // AuthScope.DemandesWorkflowFermeturePrestataire -> can close handle when over
          actions.push(ActionPrestataireCloturer)
        }

        break

      case EtatWorkflowIncidentEnum.CLOTUREE_PRESTATAIRE:
        if (scps.includes(AuthScope.DemandesWorkflowAbandon)) {
          // AuthScope.DemandesWorkflowAbandon -> can refuse and close
          actions.push(ActionAbandonner)
        }

        if (scps.includes(AuthScope.DemandesWorkflowReouverture)) {
          // AuthScope.DemandesWorkflowAbandon -> can reopen closed by provider
          actions.push(ActionReouvrir)
        }

        if (scps.includes(AuthScope.DemandesWorkflowFermetureViparis)) {
          // AuthScope.DemandesWorkflowFermetureViparis -> can close
          actions.push(ActionViparisCloturer)
        }

        break

      case EtatWorkflowIncidentEnum.EN_PAUSE:
        if (!!incident?.technicienAffecteId) {
          if (scps.includes(AuthScope.DemandesTechnicienAffectation)) {
            // AuthScope.DemandesTechnicienAffectation -> can change affected technician
            actions.push(ActionPrestataireReaffecter)
          }

          if (incident.technicienAffecteId === user.id && scps.includes(AuthScope.DemandesWorkflowPriseEnCharge)) {
            // AuthScope.DemandesWorkflowPriseEnCharge -> can start handle if user is affected technician
            actions.push(ActionPrestataireReprendre)
          }
        }

        break

      case EtatWorkflowIncidentEnum.VALIDEE:
      case EtatWorkflowIncidentEnum.FERMEE:
      case EtatWorkflowIncidentEnum.ABANDONNEE:
      case EtatWorkflowIncidentEnum.CLOTUREE_VIPARIS:
      default:
        break
    }

    return actions
  }

  const bottomButtons: BottomButton[] = incident ? getActions(incident.status, scopes) : []

  const renderStandInaccessible = () => (
    <CommentAndFileInput
      title={i18n.t('screens.incidentDetail.standInaccessible.title')}
      description={i18n.t('screens.incidentDetail.standInaccessible.description')}
      buttonLabel={i18n.t('common.validate')}
      onFinish={onStandInaccessible}
      submitStatus={editIncidentStatus}
      requiredComment
      maxFiles={1}
    />
  )

  const renderSendQuote = () => (
    <CommentAndFileInput
      title={i18n.t('screens.incidentDetail.sendQuote.title')}
      description={i18n.t('screens.incidentDetail.sendQuote.description')}
      buttonLabel={i18n.t('screens.incidentDetail.sendQuote.button')}
      onFinish={onQuoteSend}
      submitStatus={editIncidentStatus}
      maxFiles={1}
      requiredFile
    />
  )

  const renderRefuseForm = () => <RefuseForm onSubmit={refuse} submitStatus={editIncidentStatus} />

  const renderSelectTechnician = (onTechnician: (t: UserResponse) => void) => (
    <SelectTechnician options={techniciansOptions} onSubmit={onTechnician} submitStatus={editIncidentStatus} />
  )

  const renderBottomButtons = () => (
    <BottomContainer width={width}>
      {bottomButtons.map((buttonProps) => (
        <ButtonContainer key={buttonProps.label}>
          <Button {...buttonProps} />
        </ButtonContainer>
      ))}
    </BottomContainer>
  )

  const renderMobileHeader = () => {
    return (
      <HeaderContainer>
        <Back onPress={navigation.goBack}>
          <Icon color={theme.colors.background} size={35} name="arrow-left" />
        </Back>
        <Title>
          {!!incident
            ? i18n.t('screens.incidentDetail.incidentNumber', {
                incidentNumber: incident.numero,
              })
            : i18n.t('screens.incidentDetail.title')}
        </Title>
        <Buttons>
          {!!incident && !!incident.plan?.x && (
            <HeaderButton isMap onPress={navigateToMap} disabled={followStatus !== 'fetched'}>
              <Icon size={25} name="map" color={theme.colors.primary} />
            </HeaderButton>
          )}
          <HeaderButton onPress={onFollowButtonClicked} disabled={followStatus !== 'fetched'}>
            <Icon size={35} name={isIncidentFollowed ? 'love-filled' : 'fav'} color={theme.colors.background} />
          </HeaderButton>
        </Buttons>
      </HeaderContainer>
    )
  }

  return (
    <Root
      loading={status === 'loading'}
      header={
        !!incident
          ? i18n.t('screens.incidentDetail.incidentNumber', {
              incidentNumber: incident.numero,
            })
          : i18n.t('screens.incidentDetail.title')
      }
      fixed
      onLeft={navigation.goBack}
      right={headerButtons.length > 0 ? headerButtons[0].leftIcon : undefined}
      onRight={headerButtons.length > 0 ? headerButtons[0].onClick : undefined}
      renderFixedBottom={isPhone && bottomButtons.length > 0 ? renderBottomButtons : undefined}
      renderHeader={isPhone ? renderMobileHeader : undefined}>
      {status === 'fetched' && !!incident ? (
        <>
          <IncidentDetailComponent
            incident={incident}
            commentStatus={commentStatus}
            comments={comments}
            onCommentSubmitted={submitComment}
            submitStatus={submitStatus}
            headerButtons={headerButtons}
            onProviderRelaunch={onProviderRelaunch}
            onAffectTechnician={onAffect}
            isPrioritaire={isPrioritaire}
            onPriorityChange={() => setIsPrioritaire(!isPrioritaire)}
            prestataireId={prestataireId}
            setPrestataireId={reassignProvider}
            navigateToMap={navigateToMap}
            edlId={edlId}
            copyIncidentButton={canCopyIncident ? copyButton : undefined}
          />
          {!isPhone && bottomButtons.length > 0 && renderBottomButtons()}
        </>
      ) : (
        <MainContainer>
          <MissingInfo>{i18n.t('common.error')}</MissingInfo>
        </MainContainer>
      )}
    </Root>
  )
}

export default IncidentDetailScreen

const BottomContainer = styled(View)<{ width: number }>`
  z-index: 1;
  ${(props) => props.theme.constants.shadow};
  background-color: ${(props) => props.theme.colors.background};
  padding: 16px;
  flex-direction: row;
  justify-content: center;
  ${(props) => props.theme.breakpoint === 'phone' && `width: ${props.width}px;`}
`

const ButtonContainer = styled(View)`
  margin: 0 8px;
  ${(props) => (props.theme.breakpoint === 'phone' ? `flex: 1;` : `width: 180px;`)}
`

const MainContainer = styled(View)`
  display: flex;
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? `16px` : '24px')};
`

const MissingInfo = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? ` padding: 23px 16px;
          background-color: ${props.theme.colors.background};
          text-align: center;
          font-size: 12px;`
      : `padding: 16px 0px;`}
`

const HeaderContainer = styled(View)`
  flex-direction: row;
  justify-content: space-between;
  background-color: ${(props) => props.theme.colors.primary};
  align-items: center;
  padding-top: ${(props) => props.theme.insets.top}px;
`
const Back = styled(TouchableOpacity)`
  flex: 1;
`
const Title = styled(Text)`
  flex: 3;
  text-align: center;
  ${(props) => props.theme.fonts.Headline};
  color: ${(props) => props.theme.colors.background};
`
const Buttons = styled(View)`
  flex-direction: row;
  flex: 1;
  justify-content: flex-end;
  padding: 16px;
`
const HeaderButton = styled(TouchableOpacity)<{ isMap?: boolean }>`
  height: 35px;
  width: 35px;
  border-radius: 35px;
  background-color: ${(props) => (props.isMap ? props.theme.colors.background : props.theme.colors.primary)};
  justify-content: center;
  align-items: center;
  margin-right: ${(props) => (props.isMap ? 16 : 0)}px;
`
